import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Button,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Paper,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { Edit, Delete, Add, Event, EventNote } from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { lt } from 'date-fns/locale';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { getApiUrl } from '../config/api';
import ReservationFormDialog, { recurringEndFromWeeks } from './stiklainis/ReservationFormDialog';
import CalendarExportMenu from './stiklainis/CalendarExportMenu';
import { Reservation, ReservationFormData, ConferenceRoom } from './stiklainis/types';
import { formatReservationTime, formatReservationDate } from './stiklainis/formatTime';
import { upcomingCardSx, pastCardSx } from './stiklainis/stiklainisStyles';

const STIKLAINIS_ROOM_ID = '550e8400-e29b-41d4-a716-446655440001';

const MyReservations: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { user, token } = useAuth();
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [rooms, setRooms] = useState<ConferenceRoom[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogNotice, setDialogNotice] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingReservation, setEditingReservation] = useState<Reservation | null>(null);
  const [calendarMenuAnchor, setCalendarMenuAnchor] = useState<HTMLElement | null>(null);
  const [calendarMenuReservation, setCalendarMenuReservation] = useState<Reservation | null>(null);
  const emptyForm = (): ReservationFormData => ({
    title: '',
    description: '',
    startDate: new Date(),
    startTime: new Date(),
    endTime: new Date(),
    roomId: STIKLAINIS_ROOM_ID,
    attendeeCount: '',
    isRecurring: false,
    recurringPattern: 'weekly',
    recurringWeeks: 4,
  });
  const [formData, setFormData] = useState<ReservationFormData>(emptyForm);
  const [deleteSeriesPrompt, setDeleteSeriesPrompt] = useState<{
    open: boolean;
    reservationId: string | null;
  }>({ open: false, reservationId: null });

  const isPartOfSeries = (reservation: Reservation) =>
    Boolean(reservation.parentReservationId || reservation.isRecurring);

  useEffect(() => {
    if (token) loadReservations();
  }, [token]);

  const loadReservations = async () => {
    try {
      setLoading(true);
      setError(null);

      const [reservationsRes, roomsRes] = await Promise.all([
        fetch(getApiUrl('/reservations?limit=200'), { headers: { Authorization: `Bearer ${token}` } }),
        fetch(getApiUrl('/conference-rooms'), { headers: { Authorization: `Bearer ${token}` } }),
      ]);

      if (!reservationsRes.ok) throw new Error(t('stiklainis.failedToLoadReservations'));
      if (roomsRes.ok) setRooms(await roomsRes.json());

      const data: Reservation[] = await reservationsRes.json();
      const userReservations = data.filter((r) => r.user.id === user?.id);
      const now = new Date();
      const sorted = [...userReservations].sort((a, b) => {
        const aEnd = new Date(a.endTime).getTime();
        const bEnd = new Date(b.endTime).getTime();
        const aUpcoming = aEnd >= now.getTime();
        const bUpcoming = bEnd >= now.getTime();
        if (aUpcoming !== bUpcoming) return aUpcoming ? -1 : 1;
        const aStart = new Date(a.startTime).getTime();
        const bStart = new Date(b.startTime).getTime();
        return aUpcoming ? aStart - bStart : bStart - aStart;
      });
      setReservations(sorted);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : t('stiklainis.failedToLoadReservations'));
    } finally {
      setLoading(false);
    }
  };

  const { upcoming, past } = useMemo(() => {
    const now = new Date();
    return {
      upcoming: reservations.filter((r) => new Date(r.endTime) >= now),
      past: reservations.filter((r) => new Date(r.endTime) < now),
    };
  }, [reservations]);

  const getStatusText = (status: string) => {
    switch (status) {
      case 'confirmed': return t('stiklainis.statusConfirmed');
      case 'cancelled': return t('stiklainis.statusCancelled');
      case 'completed': return t('stiklainis.statusCompleted');
      default: return status;
    }
  };

  const getStatusColor = (status: string): 'success' | 'error' | 'default' => {
    switch (status) {
      case 'confirmed': return 'success';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const handleCreateReservation = () => {
    setDialogNotice(null);
    setEditingReservation(null);
    const now = new Date();
    const startTime = new Date(now.getTime() + 60 * 60 * 1000);
    const endTime = new Date(startTime.getTime() + 60 * 60 * 1000);
    setFormData({
      ...emptyForm(),
      startDate: now,
      startTime,
      endTime,
      attendeeCount: '',
    });
    setOpenDialog(true);
  };

  const handleEditReservation = (reservation: Reservation) => {
    setDialogNotice(null);
    setEditingReservation(reservation);
    const startDate = new Date(reservation.startTime);
    const endDate = new Date(reservation.endTime);
    setFormData({
      title: reservation.title,
      description: reservation.description || '',
      startDate,
      startTime: startDate,
      endTime: endDate,
      roomId: reservation.room.id,
      attendeeCount: reservation.attendeeCount ?? '',
      isRecurring: false,
      recurringPattern: 'weekly',
      recurringWeeks: 4,
    });
    setOpenDialog(true);
  };

  const handleSaveReservation = async () => {
    try {
      const startDateTime = new Date(formData.startDate);
      startDateTime.setHours(formData.startTime.getHours(), formData.startTime.getMinutes(), 0, 0);
      const endDateTime = new Date(formData.startDate);
      endDateTime.setHours(formData.endTime.getHours(), formData.endTime.getMinutes(), 0, 0);

      const attendeeCountValue = typeof formData.attendeeCount === 'string'
        ? parseInt(formData.attendeeCount, 10)
        : formData.attendeeCount;

      const reservationData: Record<string, unknown> = {
        title: formData.title,
        description: formData.description,
        startTime: startDateTime.toISOString(),
        endTime: endDateTime.toISOString(),
        roomId: formData.roomId,
        attendeeCount: Number.isFinite(attendeeCountValue as number) && (attendeeCountValue as number) >= 1 ? attendeeCountValue : 1,
      };

      if (!editingReservation && formData.isRecurring && formData.recurringWeeks >= 1) {
        const endDay = recurringEndFromWeeks(
          formData.startDate,
          formData.recurringPattern,
          formData.recurringWeeks
        );
        endDay.setHours(12, 0, 0, 0);
        reservationData.isRecurring = true;
        reservationData.recurringPattern = formData.recurringPattern;
        reservationData.recurringEndDate = endDay.toISOString();
      }

      const url = editingReservation
        ? getApiUrl(`/reservations/${editingReservation.id}`)
        : getApiUrl('/reservations');

      const response = await fetch(url, {
        method: editingReservation ? 'PUT' : 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(reservationData),
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = t('stiklainis.failedToSaveReservation');
        const isTimeSlotError = (s: string) => {
          const lower = (s || '').toLowerCase();
          return lower.includes('not available') || lower.includes('time slot') || lower.includes('conflict') || lower.includes('overlap') || lower.includes('taken') || lower.includes('already booked');
        };
        try {
          const errorData = JSON.parse(errorText);
          const errStr = errorData.error || errorData.message || '';
          if (Array.isArray(errorData.conflictDates) && errorData.conflictDates.length > 0) {
            errorMessage = t('stiklainis.seriesConflictDates', 'Užimtos datos: {{dates}}', {
              dates: errorData.conflictDates.join(', '),
            });
          } else if (errStr) {
            if (isTimeSlotError(errStr)) errorMessage = t('stiklainis.timeSlotTaken');
            else if (errStr.toLowerCase().includes('2 months') || errStr.toLowerCase().includes('recurring end')) {
              errorMessage = t(
                'stiklainis.repeatTooLong',
                'Kartojimą galima planuoti ne ilgiau kaip 2 mėnesius.'
              );
            }
            else if (errStr.toLowerCase().includes('past')) errorMessage = t('stiklainis.cannotBookPast');
            else if (errStr.toLowerCase().includes('title')) errorMessage = t('stiklainis.titleRequired');
            else errorMessage = errStr;
          }
        } catch {
          if (errorText?.length < 200 && isTimeSlotError(errorText)) errorMessage = t('stiklainis.timeSlotTaken');
        }
        setDialogNotice({ type: 'error', text: errorMessage });
        return;
      }

      setOpenDialog(false);
      setDialogNotice(null);
      loadReservations();
    } catch (err: unknown) {
      setDialogNotice({ type: 'error', text: err instanceof Error ? err.message : t('stiklainis.failedToSaveReservation') });
    }
  };

  const performDeleteReservation = async (reservationId: string, deleteSeries: boolean) => {
    try {
      const qs = deleteSeries ? '?deleteSeries=true' : '';
      const response = await fetch(getApiUrl(`/reservations/${reservationId}${qs}`), {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = t('stiklainis.failedToCancelReservation');
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.error?.includes('past') || errorData.error?.includes('current')) {
            errorMessage = t('stiklainis.cannotDeletePastReservation');
          } else if (errorData.error) {
            errorMessage = errorData.error;
          }
        } catch { /* default */ }
        throw new Error(errorMessage);
      }
      setDeleteSeriesPrompt({ open: false, reservationId: null });
      loadReservations();
    } catch (err: unknown) {
      setDeleteSeriesPrompt({ open: false, reservationId: null });
      setError(err instanceof Error ? err.message : t('stiklainis.failedToCancelReservation'));
    }
  };

  const handleDeleteReservation = (reservationId: string) => {
    const reservation = reservations.find((r) => r.id === reservationId);
    if (reservation && isPartOfSeries(reservation)) {
      setDeleteSeriesPrompt({ open: true, reservationId });
      return;
    }
    void performDeleteReservation(reservationId, false);
  };

  const renderReservationCard = (reservation: Reservation, isPastReservation: boolean) => (
    <Card key={reservation.id} elevation={0} sx={isPastReservation ? pastCardSx(theme) : upcomingCardSx(theme)}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              {reservation.title}
            </Typography>
            {reservation.description && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                {reservation.description}
              </Typography>
            )}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip label={formatReservationDate(reservation.startTime)} size="small" variant="outlined" />
              <Chip
                label={`${formatReservationTime(reservation.startTime)} – ${formatReservationTime(reservation.endTime)}`}
                size="small"
                variant="outlined"
              />
              <Chip label={reservation.room.name} size="small" variant="outlined" />
              <Chip label={getStatusText(reservation.status)} size="small" color={getStatusColor(reservation.status)} />
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
            <Tooltip title={t('calendar.addToCalendar')}>
              <IconButton size="small" onClick={(e) => { setCalendarMenuAnchor(e.currentTarget); setCalendarMenuReservation(reservation); }}>
                <Event />
              </IconButton>
            </Tooltip>
            {!isPastReservation && (
              <>
                <Tooltip title={t('stiklainis.editReservation')}>
                  <IconButton size="small" onClick={() => handleEditReservation(reservation)}>
                    <Edit />
                  </IconButton>
                </Tooltip>
                <Tooltip title={t('stiklainis.cancelReservation')}>
                  <IconButton size="small" color="error" onClick={() => handleDeleteReservation(reservation.id)}>
                    <Delete />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  if (!token) {
    return <Alert severity="warning">{t('stiklainis.notLoggedIn')}</Alert>;
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={lt}>
      <Box>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {t('stiklainis.myReservationsTitle')}
          </Typography>
          <Button variant="contained" startIcon={<Add />} onClick={handleCreateReservation}>
            {t('stiklainis.newReservation')}
          </Button>
        </Box>

        {reservations.length === 0 ? (
          <Paper elevation={0} sx={{ border: 1, borderColor: 'divider', borderRadius: 2, textAlign: 'center', py: 6, px: 3 }}>
            <EventNote sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {t('stiklainis.noReservations')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 400, mx: 'auto' }}>
              {t('stiklainis.noReservationsHint')}
            </Typography>
            <Button variant="contained" startIcon={<Add />} onClick={handleCreateReservation}>
              {t('stiklainis.newReservation')}
            </Button>
          </Paper>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {upcoming.length > 0 && (
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5, color: 'success.dark' }}>
                  {t('stiklainis.upcomingReservations')} ({upcoming.length})
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {upcoming.map((r) => renderReservationCard(r, false))}
                </Box>
              </Box>
            )}
            {past.length > 0 && (
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5, color: 'text.secondary' }}>
                  {t('stiklainis.pastReservations')} ({past.length})
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {past.map((r) => renderReservationCard(r, true))}
                </Box>
              </Box>
            )}
          </Box>
        )}

        <ReservationFormDialog
          open={openDialog}
          editingReservation={editingReservation}
          formData={formData}
          rooms={rooms.length > 0 ? rooms : [{ id: STIKLAINIS_ROOM_ID, name: 'Stiklainis', capacity: 12, location: '', amenities: '', description: '' }]}
          notice={dialogNotice}
          onClose={() => { setOpenDialog(false); setDialogNotice(null); }}
          onSave={handleSaveReservation}
          onFormChange={(data) => setFormData((prev) => ({ ...prev, ...data }))}
          onClearNotice={() => setDialogNotice(null)}
        />

        <CalendarExportMenu
          anchorEl={calendarMenuAnchor}
          reservation={calendarMenuReservation}
          onClose={() => { setCalendarMenuAnchor(null); setCalendarMenuReservation(null); }}
        />

        <Dialog
          open={deleteSeriesPrompt.open}
          onClose={() => setDeleteSeriesPrompt({ open: false, reservationId: null })}
        >
          <DialogTitle>{t('stiklainis.deleteSeriesTitle', 'Ištrinti seriją?')}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {t(
                'stiklainis.deleteSeriesBody',
                'Šis susitikimas priklauso kartojamai serijai. Ištrinti tik šį, ar visą seriją?'
              )}
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ flexWrap: 'wrap', gap: 1, px: 2, pb: 2 }}>
            <Button onClick={() => setDeleteSeriesPrompt({ open: false, reservationId: null })}>
              {t('common.cancel')}
            </Button>
            <Button
              color="error"
              variant="outlined"
              onClick={() => {
                if (deleteSeriesPrompt.reservationId) {
                  void performDeleteReservation(deleteSeriesPrompt.reservationId, false);
                }
              }}
            >
              {t('stiklainis.deleteThisOnly', 'Tik šį')}
            </Button>
            <Button
              color="error"
              variant="contained"
              onClick={() => {
                if (deleteSeriesPrompt.reservationId) {
                  void performDeleteReservation(deleteSeriesPrompt.reservationId, true);
                }
              }}
            >
              {t('stiklainis.deleteEntireSeries', 'Visą seriją')}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default MyReservations;
